/**
 * accept.blue Hosted Tokenization v0.3, as this app mounts it (M7.7, FR-PAY-01,
 * ADR-010, SECURITY.md §10, `Docs/_research/acceptblue-mailgun.md` §1).
 *
 * ## The whole point
 *
 * The card number and the security code are typed into **the vendor's iframe**, not into
 * this application. They go browser → accept.blue and come back as a `nonce-…`: a single
 * use reference, valid fifteen minutes, which the server exchanges for a vault entry. No
 * PAN, no CVV and no expiry ever touches our DOM, our state or our logs — which is what
 * keeps the containing page out of PCI scope beyond its script-integrity duties.
 *
 * ## The script is injected here, never in `index.html`
 *
 * Two reasons, and both are decisions rather than taste:
 *
 *  1. **The URL is the clinic's, not the bundle's.** `GET /public/payments/config` answers
 *     the exact production or sandbox script URL for the tenant, and the CSP allowance is
 *     built from that same value (`Docs/BOOKING_FUNNEL_WIRING.md` §6). A `<script>` in
 *     `index.html` would hard-code a host and put a key-shaped decision in a bundle.
 *  2. **No third-party script loads on a page that has not asked for one** (ADR-019). The
 *     loader runs when a patient reaches a card form at a clinic that takes cards, and at
 *     no other time — not on `/book`, not on the portal home, and never at all for a
 *     clinic with `enabled: false`.
 *
 * The tag carries the document's CSP nonce when the static host injects one
 * (`<meta name="csp-nonce">`), so a policy without `'unsafe-inline'` still admits it. It
 * carries **no** `integrity`: accept.blue publishes no SRI hash for the v0.3 loader, and
 * an invented one would simply break the page. The URL is pinned to `v0.3` and the script
 * is monitored instead, which is what PCI DSS 4.0.1 6.4.3 / 11.6.1 ask of us
 * (SECURITY.md §10).
 */

/* ------------------------------------------------------------------ *
 * What the vendor gives us
 * ------------------------------------------------------------------ */

/**
 * `getNonceToken()`'s answer, as the live heyallergy.com page reads it
 * (`apps/patient/parity/payment/inline-scripts.js`).
 *
 * Everything but the nonce is optional, because the only field this platform needs is
 * the one the server can exchange. The expiry is read from here rather than collected
 * again in our own inputs: the hosted form already owns the expiry fields (v0.3 renders
 * card, expiry month, expiry year and CVV together), and a second pair of inputs would
 * be a second answer that could disagree with the card that was actually tokenized.
 */
export interface TokenizedCard {
  nonce: string;
  last4: string | null;
  cardType: string | null;
  expiryMonth: number | null;
  expiryYear: number | null;
}

/** Inline styles for the hosted fields. Passed straight to the vendor's `styles`. */
export type TokenizerStyles = Record<string, string>;

export interface MountTokenizerOptions {
  /** Exactly what `GET /public/payments/config` returned. Never a constant. */
  scriptUrl: string;
  /** The `pk_…` tokenization key. The only vendor key a browser ever holds. */
  publicKey: string;
  /** CSS selector of the element the iframe mounts into. */
  target: string;
  styles?: TokenizerStyles;
  /** Whether the hosted form renders its own ZIP field. The funnel collects its own. */
  showZip?: boolean;
  /** Called on every keystroke and blur inside the hosted fields. */
  onFieldChange?: (state: CardFieldState) => void;
}

/** A hosted field the vendor can flag. Our own names, so a surface never sees theirs. */
export type CardField = 'number' | 'expiry' | 'cvv';

/**
 * What the hosted form says about itself while the patient types (v0.3 `input` and
 * `change` events). Only a brand and the names of the fields it considers wrong: the
 * event also carries a masked number, which nothing here has a use for.
 */
export interface CardFieldState {
  /** `Visa`, `MasterCard`, `Amex`, `Discover`…, or null until the number says. */
  brand: string | null;
  /** Fields the vendor has validated and refused. It validates on blur or on a full number. */
  invalid: readonly CardField[];
}

export interface MountedTokenizer {
  /** Tokenize what the patient typed. Rejects when the vendor refuses the card. */
  getNonceToken(): Promise<TokenizedCard>;
  /** Tear the iframe down when the form leaves the screen. */
  destroy(): void;
}

export type MountTokenizer = (options: MountTokenizerOptions) => Promise<MountedTokenizer>;

/* ------------------------------------------------------------------ *
 * The vendor's global
 * ------------------------------------------------------------------ */

interface VendorTokenizer {
  getNonceToken(): Promise<unknown>;
  on?(event: string, handler: (payload: unknown) => void): void;
}

type VendorConstructor = new (
  publicKey: string,
  options: Record<string, unknown>,
) => VendorTokenizer;

function vendor(): VendorConstructor | null {
  const global = window as unknown as { HostedTokenization?: VendorConstructor };
  return typeof global.HostedTokenization === 'function' ? global.HostedTokenization : null;
}

/* ------------------------------------------------------------------ *
 * Loading the script
 * ------------------------------------------------------------------ */

/** One in-flight load per URL: two card forms on one document share the one script. */
const loads = new Map<string, Promise<void>>();

/** Seconds we will wait for the vendor before giving the patient an answer. */
const LOAD_TIMEOUT_MS = 15_000;

/**
 * Load the tokenization script once.
 *
 * The URL is checked for `https:` before it is used. That is not a substitute for the
 * CSP — the policy is the control — but a config that somehow named `http:` would be a
 * card form loaded over a channel an attacker can rewrite, and refusing is cheaper than
 * explaining.
 */
export async function loadTokenizationScript(scriptUrl: string): Promise<void> {
  const url = new URL(scriptUrl);
  if (url.protocol !== 'https:') {
    throw new Error('The tokenization script must be served over https.');
  }

  const existing = loads.get(url.href);
  if (existing !== undefined) return existing;

  const load = new Promise<void>((resolve, reject) => {
    if (vendor() !== null) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = url.href;
    script.async = true;
    /* No `crossorigin`: accept.blue serves this script without CORS headers, so a CORS
       load is blocked by the browser and the card form never starts (checked live in
       Chrome, 2026-10-01). */
    const nonce = document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content');
    if (nonce !== null && nonce !== undefined && nonce !== '') script.nonce = nonce;

    const timer = setTimeout(() => {
      reject(new Error('The payment form did not load in time.'));
    }, LOAD_TIMEOUT_MS);

    script.addEventListener('load', () => {
      clearTimeout(timer);
      if (vendor() === null) {
        reject(new Error('The payment form loaded but did not start.'));
        return;
      }
      resolve();
    });
    script.addEventListener('error', () => {
      clearTimeout(timer);
      reject(new Error('The payment form could not be loaded.'));
    });

    document.head.append(script);
  });

  /* A failed load is not cached: the next attempt gets a fresh tag, so a flaky network
     does not leave the page permanently unable to take a card. */
  loads.set(url.href, load);
  load.catch(() => {
    loads.delete(url.href);
  });
  return load;
}

/* ------------------------------------------------------------------ *
 * Mounting
 * ------------------------------------------------------------------ */

/** How long the hosted fields have to say they are ready before we stop waiting. */
const READY_TIMEOUT_MS = 12_000;

/**
 * Mount the hosted fields into `target` and hand back the two operations a form needs.
 *
 * The options mirror the live page's own call (`inline-scripts.js`): `requireCvv2` on,
 * the ZIP left to our form, and static top labels — which is what makes the vendor's
 * fields land in the same box, at the same size, as the captured baseline.
 */
export const mountHostedTokenizer: MountTokenizer = async (options) => {
  await loadTokenizationScript(options.scriptUrl);

  const HostedTokenization = vendor();
  if (HostedTokenization === null) {
    throw new Error('The payment form could not be started.');
  }

  const host = document.querySelector(options.target);
  if (host === null) throw new Error('The payment form has nowhere to mount.');

  const instance = new HostedTokenization(options.publicKey, {
    target: options.target,
    requireCvv2: true,
    showZip: options.showZip ?? false,
    ...(options.styles === undefined ? {} : { styles: options.styles }),
  });

  if (options.onFieldChange !== undefined) {
    const report = options.onFieldChange;
    const handler = (payload: unknown): void => {
      report(readFieldState(payload));
    };
    try {
      instance.on?.('input', handler);
      instance.on?.('change', handler);
    } catch {
      /* A version without these events still takes a card; it just cannot narrate. */
    }
  }

  /*
   * `ready` is best-effort: v0.3 fires it, but a version that does not must not leave a
   * patient looking at a spinner for ever, so the wait resolves on a timeout as well.
   * The iframe is in the DOM either way by the time the constructor returns.
   */
  await new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, READY_TIMEOUT_MS);
    try {
      instance.on?.('ready', () => {
        clearTimeout(timer);
        resolve();
      });
    } catch {
      clearTimeout(timer);
      resolve();
    }
  });

  return {
    getNonceToken: async () => readTokenizedCard(await instance.getNonceToken()),
    destroy: () => {
      /*
       * v0.3 exposes no teardown, so the iframe is removed with its host's children. The
       * instance then holds a reference to nothing and is collected with the component.
       */
      host.replaceChildren();
    },
  };
};

/**
 * Read the vendor's answer defensively.
 *
 * Only the nonce is required. The brand, the last four and the expiry are read when they
 * are there and left null when they are not: `PaymentMethodSummary` is nullable in all
 * three for the same reason, and a card the platform can charge but cannot describe is
 * still a card the platform can charge.
 */
function readTokenizedCard(raw: unknown): TokenizedCard {
  const value = (raw ?? {}) as Record<string, unknown>;
  const bare = typeof value['nonce'] === 'string' ? value['nonce'] : null;
  if (bare === null || bare === '') {
    throw new Error('The payment form returned no token.');
  }

  return {
    nonce: asSource(bare),
    last4: digits(value['last4'], 4),
    cardType: typeof value['cardType'] === 'string' ? value['cardType'] : null,
    expiryMonth: month(value['expiryMonth']),
    expiryYear: year(value['expiryYear']),
  };
}

/** The vendor's field keys, as v0.3 lists them in `fieldErrors`. */
const VENDOR_FIELDS: Record<string, CardField> = {
  card: 'number',
  expiryMonth: 'expiry',
  expiryYear: 'expiry',
  cvv2: 'cvv',
};

/** v0.3 event payload: `{ result: { cardType, … }, fieldErrors?: string[] }`. */
export function readFieldState(raw: unknown): CardFieldState {
  const payload = (raw ?? {}) as Record<string, unknown>;
  const result = (payload['result'] ?? {}) as Record<string, unknown>;
  const type = result['cardType'];
  const brand = typeof type === 'string' && type !== '' && !/unknown/i.test(type) ? type : null;

  const listed = Array.isArray(payload['fieldErrors']) ? payload['fieldErrors'] : [];
  const invalid: CardField[] = [];
  for (const key of listed) {
    const field = typeof key === 'string' ? VENDOR_FIELDS[key] : undefined;
    if (field !== undefined && !invalid.includes(field)) invalid.push(field);
  }
  return { brand, invalid };
}

/**
 * The vault API takes a card source as `nonce-…`, but live v0.3 answers with the bare
 * token (20 characters, no prefix; checked against the production key on 2026-10-01).
 * Without the prefix the adapter refuses every real card before it leaves the server.
 */
function asSource(nonce: string): string {
  return /^(nonce|tkn|ref)-/.test(nonce) ? nonce : `nonce-${nonce}`;
}

function digits(value: unknown, length: number): string | null {
  const text = typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';
  return new RegExp(`^\\d{${String(length)}}$`).test(text) ? text : null;
}

function month(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 12 ? parsed : null;
}

/** Two-digit years are the vendor's other spelling; 28 is 2028, never 28 AD. */
function year(value: unknown): number | null {
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) return null;
  const full = parsed < 100 ? 2000 + parsed : parsed;
  return full >= 2000 && full <= 2100 ? full : null;
}
